import { useEffect, useRef, useState, useCallback } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { authFetch } from "@/lib/authFetch";
import { X, Keyboard, RotateCcw, Bot, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";

/* --------------------------------------------------------------------------
 * Mode debug : ?debug=1 dans l'URL
 * -------------------------------------------------------------------------- */
const isDebugMode =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("debug") === "1";

/* --------------------------------------------------------------------------
 * Bip sonore via Web Audio API
 * -------------------------------------------------------------------------- */
function beep() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.value = 1200;
    gain.gain.value = 0.3;
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.12);
    setTimeout(() => ctx.close(), 500);
  } catch {}
}

/* --------------------------------------------------------------------------
 * Types
 * -------------------------------------------------------------------------- */
type ScannerStatus =
  | "initializing"
  | "scanning"
  | "detected"
  | "error"
  | "permission_denied";

interface BarcodeScannerProps {
  onClose: () => void;
}

interface LookupResult {
  found: boolean;
  sn: string;
  source?: string;
  robotId?: string;
  puduRobot?: {
    robot_sn: string;
    robot_type: string;
    robot_name: string;
    robot_mac?: string;
    pudu_shops?: { shop_name: string };
  };
  localRobot?: {
    id: string;
    model_name?: string;
    modelName?: string;
    serial_number?: string;
    serialNumber?: string;
  };
  suggestion?: string;
}

/* --------------------------------------------------------------------------
 * PARTIE 2 — Détection robuste du support BarcodeDetector
 * -------------------------------------------------------------------------- */
async function detectBarcodeDetectorSupport(): Promise<{
  supported: boolean;
  reason?: string;
  detector?: any;
  supportedFormats?: string[];
}> {
  if (!("BarcodeDetector" in window)) {
    return { supported: false, reason: "API non exposée" };
  }

  let supportedFormats: string[] = [];
  try {
    supportedFormats = await (window as any).BarcodeDetector.getSupportedFormats();
    console.log("[BarcodeDetector] Formats supportés:", supportedFormats);
  } catch (err: any) {
    return { supported: false, reason: `getSupportedFormats échoué: ${err.message}` };
  }

  if (!supportedFormats.includes("code_128")) {
    return {
      supported: false,
      reason: "code_128 absent des formats supportés",
      supportedFormats,
    };
  }

  try {
    const detector = new (window as any).BarcodeDetector({
      formats: ["code_128", "ean_13", "qr_code", "data_matrix"],
    });
    console.log("[BarcodeDetector] Instanciation OK");
    return { supported: true, detector, supportedFormats };
  } catch (err: any) {
    return { supported: false, reason: `Erreur init: ${err.message}`, supportedFormats };
  }
}

/* --------------------------------------------------------------------------
 * Composant principal
 * -------------------------------------------------------------------------- */
export function BarcodeScanner({ onClose }: BarcodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const rafRef = useRef<number | null>(null);
  const scanningRef = useRef(false);
  const [, navigate] = useLocation();

  const [phase, setPhase] = useState<"scanning" | "result" | "manual">("scanning");
  const [scannerStatus, setScannerStatus] = useState<ScannerStatus>("initializing");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [manualSn, setManualSn] = useState("");
  const [isLooking, setIsLooking] = useState(false);
  const [scanResult, setScanResult] = useState<LookupResult | null>(null);
  const [lastScans, setLastScans] = useState<string[]>([]);
  const [frameCount, setFrameCount] = useState(0);
  const [usedEngine, setUsedEngine] = useState<"native" | "zxing" | "">(""); 
  const [diagnosticReason, setDiagnosticReason] = useState<string>("");
  const [supportedFormats, setSupportedFormats] = useState<string[]>([]);
  const [videoResolution, setVideoResolution] = useState<string>("");
  const [lastDetectTime, setLastDetectTime] = useState<number>(0);
  const [hintLevel, setHintLevel] = useState<0 | 1 | 2>(0);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ---- lookup API ---- */
  const addToLastScans = useCallback((sn: string) => {
    setLastScans((prev) => [sn, ...prev.filter((s) => s !== sn)].slice(0, 5));
  }, []);

  const handleLookup = useCallback(
    async (sn: string) => {
      if (isLooking) return;
      setIsLooking(true);
      addToLastScans(sn);
      try {
        const res = await authFetch(`/api/robots/lookup/${encodeURIComponent(sn)}`);
        const data = await res.json();
        setScanResult({ ...data, sn });
      } catch {
        setScanResult({ found: false, sn, suggestion: "manual_creation" });
      } finally {
        setIsLooking(false);
        setPhase("result");
      }
    },
    [isLooking, addToLastScans]
  );

  const triggerScan = useCallback(
    (sn: string) => {
      if (scanningRef.current) return;
      scanningRef.current = true;
      setScannerStatus("detected");
      if ("vibrate" in navigator) navigator.vibrate(100);
      beep();
      handleLookup(sn);
    },
    [handleLookup]
  );

  /* ---- LOT 1 : Cleanup rigoureux — ordre critique iOS Safari ---- */
  const cleanupCamera = useCallback(() => {
    // 1. Stopper la boucle RAF
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    // 2. Réinitialiser zxing si actif
    if (readerRef.current) {
      try { readerRef.current.reset(); } catch {}
      readerRef.current = null;
    }
    // 3. Détacher le srcObject AVANT de stopper les tracks (iOS exige cet ordre)
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
    // 4. Stopper chaque track + removeTrack (pour iOS Safari spécifiquement)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        streamRef.current?.removeTrack(track);
      });
      streamRef.current = null;
    }
    // 5. Nettoyer le timer de hint
    if (hintTimerRef.current) { clearTimeout(hintTimerRef.current); hintTimerRef.current = null; }
  }, []);

  /* ---- Ouvrir le flux caméra ---- */
  const openCameraStream = async (preferHighRes = true): Promise<MediaStream> => {
    const constraints = {
      video: {
        facingMode: { ideal: "environment" },
        width: { ideal: preferHighRes ? 1920 : 1280 },
        height: { ideal: preferHighRes ? 1080 : 720 },
      },
      audio: false,
    };
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err: any) {
      // OverconstrainedError → réessayer sans contraintes de résolution
      if (err.name === "OverconstrainedError") {
        return await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      }
      throw err;
    }
  };

  /* ---- Attacher le stream à la balise <video> ---- */
  const attachStreamToVideo = async (stream: MediaStream) => {
    if (!videoRef.current) throw new Error("videoRef is null");
    videoRef.current.srcObject = stream;
    await videoRef.current.play();
    // Capturer la résolution réelle pour le debug
    const track = stream.getVideoTracks()[0];
    if (track) {
      const settings = track.getSettings();
      if (settings.width && settings.height) {
        setVideoResolution(`${settings.width}×${settings.height}`);
      }
    }
  };

  /* ==========================================================================
   * PARTIE 3 — MOTEUR A : BarcodeDetector natif avec throttling 200 ms
   * ========================================================================== */
  const startNativeDetector = useCallback(
    (detector: any) => {
      if (!videoRef.current) return;
      setUsedEngine("native");
      console.log("[BarcodeScanner] Démarrage moteur natif BarcodeDetector");

      const DETECT_INTERVAL_MS = 200;
      let lastTime = 0;

      const detectLoop = async (timestamp: number) => {
        if (!videoRef.current || scanningRef.current) return;

        if (videoRef.current.readyState < 2) {
          rafRef.current = requestAnimationFrame(detectLoop);
          return;
        }

        if (timestamp - lastTime < DETECT_INTERVAL_MS) {
          rafRef.current = requestAnimationFrame(detectLoop);
          return;
        }
        lastTime = timestamp;

        try {
          const barcodes: any[] = await detector.detect(videoRef.current);
          setFrameCount((c) => c + 1);
          setLastDetectTime(Date.now());

          if (barcodes.length > 0) {
            const sn = barcodes[0].rawValue;
            console.log("[BarcodeDetector] Code détecté:", sn, "format:", barcodes[0].format);
            triggerScan(sn);
            return;
          }
        } catch (e: any) {
          console.warn("[BarcodeDetector] Erreur frame:", e.message);
        }

        rafRef.current = requestAnimationFrame(detectLoop);
      };

      rafRef.current = requestAnimationFrame(detectLoop);
    },
    [triggerScan]
  );

  /* ==========================================================================
   * PARTIE 4 — MOTEUR B : zxing — fallback amélioré
   * Résolution réduite à 1280×720 pour éviter saturation CPU.
   * Hints enrichis : ALSO_INVERTED pour codes sombres/clairs inversés.
   * ========================================================================== */
  const startZxingDecoder = useCallback(
    async () => {
      if (!videoRef.current) return;
      setUsedEngine("zxing");
      console.log("[BarcodeScanner] Démarrage moteur zxing (fallback)");

      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.CODE_128,
        BarcodeFormat.EAN_13,
        BarcodeFormat.QR_CODE,
        BarcodeFormat.DATA_MATRIX,
      ]);
      hints.set(DecodeHintType.TRY_HARDER, true);
      hints.set(DecodeHintType.ALSO_INVERTED, true);
      hints.set(DecodeHintType.PURE_BARCODE, false);

      const reader = new BrowserMultiFormatReader(hints, 200);
      readerRef.current = reader;

      reader.decodeFromVideoElement(videoRef.current, (result, error) => {
        setFrameCount((c) => c + 1);
        if (error && error.name !== "NotFoundException") {
          console.warn("[BarcodeScanner/zxing]", error.name, error.message);
          return;
        }
        if (result && !scanningRef.current) {
          console.log("[zxing] Code détecté:", result.getText());
          setLastDetectTime(Date.now());
          triggerScan(result.getText());
        }
      });
    },
    [triggerScan]
  );

  /* ---- Initialisation principale ---- */
  const startScanner = useCallback(async () => {
    setScannerStatus("initializing");
    setFrameCount(0);
    setLastDetectTime(0);
    scanningRef.current = false;

    try {
      // PARTIE 2 — Détection robuste avant d'ouvrir la caméra
      const support = await detectBarcodeDetectorSupport();
      console.log("[BarcodeScanner] Support BarcodeDetector:", support);

      if (support.supportedFormats) setSupportedFormats(support.supportedFormats);

      let stream: MediaStream;
      try {
        stream = await openCameraStream(support.supported);
      } catch (camErr: any) {
        // LOT 1 — Distinguer les erreurs getUserMedia
        switch (camErr.name) {
          case "NotAllowedError":
          case "PermissionDeniedError":
            setScannerStatus("permission_denied");
            return;
          case "NotReadableError":
          case "TrackStartError":
            setScannerStatus("error");
            setErrorMessage("Caméra occupée par une autre application. Fermez les autres apps utilisant la caméra et réessayez.");
            return;
          case "AbortError":
            setScannerStatus("error");
            setErrorMessage("Initialisation interrompue. Réessayez.");
            return;
          default:
            setScannerStatus("error");
            setErrorMessage(`Erreur caméra: ${camErr.message || camErr.name}`);
            console.error("[BarcodeScanner] getUserMedia failed:", camErr);
            return;
        }
      }

      streamRef.current = stream;
      const track = stream.getVideoTracks()[0];
      if (track) {
        const caps = track.getCapabilities?.() as any;
        if (caps?.torch) setTorchSupported(true);
      }
      await attachStreamToVideo(stream);
      setScannerStatus("scanning");

      if (support.supported && support.detector) {
        setDiagnosticReason("instancié OK");
        startNativeDetector(support.detector);
      } else {
        setDiagnosticReason(support.reason || "inconnu");
        console.warn("[BarcodeScanner] BarcodeDetector non disponible:", support.reason);
        await startZxingDecoder();
      }
    } catch (e: any) {
      setScannerStatus("error");
      setErrorMessage(e.message || "Erreur inconnue");
      console.error("[BarcodeScanner] init failed:", e);
    }
  }, [startNativeDetector, startZxingDecoder]);

  /* ---- LOT 1 : Bouton "Réessayer" — cleanup + 500ms + restart ---- */
  const handleRetry = useCallback(async () => {
    cleanupCamera();
    await new Promise(r => setTimeout(r, 500));
    await startScanner();
  }, [cleanupCamera, startScanner]);

  /* ---- LOT 4 : Timer d'aide visuelle ---- */
  useEffect(() => {
    if (scannerStatus === "scanning" && phase === "scanning") {
      setHintLevel(1);
      hintTimerRef.current = setTimeout(() => setHintLevel(2), 8000);
    } else {
      setHintLevel(0);
      if (hintTimerRef.current) { clearTimeout(hintTimerRef.current); hintTimerRef.current = null; }
    }
    return () => { if (hintTimerRef.current) { clearTimeout(hintTimerRef.current); hintTimerRef.current = null; } };
  }, [scannerStatus, phase]);

  // Réinitialiser le timer à chaque détection réussie
  useEffect(() => {
    if (lastDetectTime > 0) {
      setHintLevel(1);
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
      hintTimerRef.current = setTimeout(() => setHintLevel(2), 8000);
    }
  }, [lastDetectTime]);

  useEffect(() => {
    startScanner();
    return () => cleanupCamera();
  }, []);

  /* ---- Torche ---- */
  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn } as any] });
      setTorchOn(!torchOn);
    } catch {}
  };

  /* ---- Saisie manuelle ---- */
  const handleManualSubmit = () => {
    const sn = manualSn.trim().toUpperCase();
    if (!sn) return;
    if ("vibrate" in navigator) navigator.vibrate(50);
    beep();
    setPhase("scanning");
    triggerScan(sn);
  };

  /* ---- Reset ---- */
  const resetScanner = () => {
    scanningRef.current = false;
    setScanResult(null);
    setManualSn("");
    setScannerStatus("scanning");
    setPhase("scanning");
  };

  /* ---- Navigation ---- */
  const handleOpenRobot = (localId?: string, sn?: string) => {
    onClose();
    if (localId) navigate(`/robots?openRobot=${localId}`);
    else if (sn) navigate(`/robots?openSn=${sn}`);
  };

  const handleCreateRobot = (sn: string) => {
    onClose();
    navigate(`/robots?createWithSn=${encodeURIComponent(sn)}`);
  };

  const localId = scanResult?.localRobot?.id || scanResult?.robotId;
  const localModelName =
    scanResult?.localRobot?.modelName || scanResult?.localRobot?.model_name;

  /* --------------------------------------------------------------------------
   * Label diagnostic pour le compteur de frames (Partie 1)
   * -------------------------------------------------------------------------- */
  const engineDiagLabel = () => {
    if (!usedEngine) return "";
    if (usedEngine === "native") return "native ✓";
    // zxing fallback — expliquer pourquoi
    if (diagnosticReason) return `zxing (${diagnosticReason})`;
    return "zxing";
  };

  /* ==========================================================================
   * Rendu
   * ========================================================================== */
  return (
    <div className="fixed inset-0 z-[200] bg-black flex flex-col">
      {/* ---- Header ---- */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/80 shrink-0">
        <span className="text-white font-semibold text-base">Scanner un robot</span>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="text-white hover:bg-white/10"
          data-testid="button-scanner-close"
        >
          <X className="w-6 h-6" />
        </Button>
      </div>

      <div className="flex-1 relative flex flex-col overflow-hidden">

        {/* ---- Accès caméra refusé ---- */}
        {scannerStatus === "permission_denied" ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400" />
            <div className="space-y-2">
              <p className="text-white text-lg font-semibold">Accès à la caméra refusé</p>
              <p className="text-white/60 text-sm">
                Autorisez l'accès à la caméra dans les réglages Safari (Réglages → Safari → Caméra), puis réessayez.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleRetry}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Réessayer
            </Button>
            <Button
              variant="ghost"
              onClick={() => setPhase("manual")}
              className="text-white/70 hover:text-white"
            >
              Saisir le SN manuellement
            </Button>
          </div>

        ) : scannerStatus === "error" ? (
          /* ---- Erreur d'initialisation ---- */
          <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 text-center">
            <AlertCircle className="w-16 h-16 text-orange-400" />
            <div className="space-y-2">
              <p className="text-white text-lg font-semibold">Impossible d'ouvrir la caméra</p>
              {errorMessage && (
                <p className="text-white/60 text-sm px-2">{errorMessage}</p>
              )}
            </div>
            <Button
              variant="outline"
              onClick={handleRetry}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Réessayer
            </Button>
            <Button
              variant="ghost"
              onClick={() => setPhase("manual")}
              className="text-white/70 hover:text-white"
            >
              Saisir le SN manuellement
            </Button>
          </div>

        ) : phase === "result" && scanResult ? (
          /* ---- Écran résultat ---- */
          <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6 py-8 overflow-y-auto">
            {scanResult.found ? (
              <div className="w-full max-w-sm space-y-4">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Bot className="w-8 h-8 text-green-400" />
                  </div>
                  <p className="text-white font-semibold text-lg">Robot identifié</p>
                  <span className="font-mono text-green-400 text-sm bg-green-500/10 px-3 py-1 rounded-full">
                    {scanResult.sn}
                  </span>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-2">
                  {scanResult.puduRobot && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Modèle</span>
                        <span className="text-white font-medium">
                          {scanResult.puduRobot.robot_type || "—"}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Nom Pudu</span>
                        <span className="text-white">{scanResult.puduRobot.robot_name || "—"}</span>
                      </div>
                      {scanResult.puduRobot.pudu_shops && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/50">Shop</span>
                          <span className="text-white">
                            {scanResult.puduRobot.pudu_shops.shop_name}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  {localModelName && (
                    <div className="flex justify-between text-sm">
                      <span className="text-white/50">Fiche locale</span>
                      <span className="text-white font-medium">{localModelName}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Source</span>
                    <span className="text-white/50 text-xs">{scanResult.source}</span>
                  </div>
                </div>
                {localId && (
                  <Button
                    className="w-full"
                    onClick={() => handleOpenRobot(localId, scanResult.sn)}
                    data-testid="button-open-robot-fiche"
                  >
                    Ouvrir la fiche robot
                  </Button>
                )}
                {!localId && (
                  <Button
                    className="w-full"
                    onClick={() => handleCreateRobot(scanResult.sn)}
                    data-testid="button-create-robot-from-scan"
                  >
                    Créer une fiche pour ce robot
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="w-full text-white/70 hover:text-white"
                  onClick={resetScanner}
                  data-testid="button-scan-again"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Scanner un autre robot
                </Button>
              </div>
            ) : (
              <div className="w-full max-w-sm space-y-4">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-orange-400" />
                  </div>
                  <p className="text-white font-semibold text-lg">Robot inconnu</p>
                  <span className="font-mono text-orange-400 text-sm bg-orange-500/10 px-3 py-1 rounded-full">
                    {scanResult.sn}
                  </span>
                  <p className="text-white/50 text-sm text-center">
                    Aucun robot trouvé avec ce numéro de série dans notre base.
                  </p>
                </div>
                <Button
                  className="w-full"
                  onClick={() => handleCreateRobot(scanResult.sn)}
                  data-testid="button-create-unknown-robot"
                >
                  Créer une fiche manuellement
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-white/70 hover:text-white"
                  onClick={resetScanner}
                  data-testid="button-retry-scan"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Réessayer
                </Button>
              </div>
            )}
          </div>

        ) : phase === "manual" ? (
          /* ---- Saisie manuelle ---- */
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
            <p className="text-white font-medium text-lg">Saisir le numéro de série</p>
            <p className="text-white/50 text-sm text-center">
              Entrez le SN imprimé sur l'étiquette du robot
            </p>
            <Input
              autoFocus
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40 text-center font-mono text-lg h-12 max-w-sm w-full"
              placeholder="Ex: 8BR065601060002"
              value={manualSn}
              onChange={(e) => setManualSn(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && handleManualSubmit()}
              data-testid="input-manual-sn"
            />
            <Button
              className="max-w-sm w-full"
              onClick={handleManualSubmit}
              disabled={!manualSn.trim() || isLooking}
              data-testid="button-manual-search"
            >
              {isLooking ? "Recherche…" : "Rechercher"}
            </Button>
            <Button
              variant="ghost"
              className="text-white/60 hover:text-white"
              onClick={() => setPhase("scanning")}
            >
              Retour au scanner
            </Button>
          </div>

        ) : (
          /* ---- Vue caméra ---- */
          <div className="flex-1 relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 w-full h-full object-cover"
            />

            {/* Overlay de dimming avec cadre de visée */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <mask id="scan-cutout">
                  <rect width="100%" height="100%" fill="white" />
                  <rect x="10%" y="28%" width="80%" height="44%" rx="12" fill="black" />
                </mask>
              </defs>
              <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#scan-cutout)" />
              <rect
                x="10%" y="28%" width="80%" height="44%"
                rx="12" fill="none"
                stroke={scannerStatus === "detected" ? "#22c55e" : "rgba(255,255,255,0.4)"}
                strokeWidth="1"
              />
              {/* Coins bleus TL */}
              <polyline points="10%,34% 10%,28% 16%,28%" fill="none" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              {/* TR */}
              <polyline points="84%,28% 90%,28% 90%,34%" fill="none" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              {/* BL */}
              <polyline points="10%,66% 10%,72% 16%,72%" fill="none" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              {/* BR */}
              <polyline points="84%,72% 90%,72% 90%,66%" fill="none" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

            {/* Badge de statut au-dessus du cadre */}
            <div className="absolute top-[24%] w-full flex flex-col items-center gap-1.5 pointer-events-none">
              {scannerStatus === "initializing" && (
                <span className="flex items-center gap-2 text-white/80 text-sm bg-black/50 px-3 py-1.5 rounded-full">
                  <span className="w-3 h-3 border-2 border-white/60 border-t-transparent rounded-full animate-spin inline-block" />
                  Initialisation de la caméra…
                </span>
              )}
              {scannerStatus === "scanning" && (
                <span className="flex items-center gap-2 text-white/80 text-sm bg-black/50 px-3 py-1.5 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse inline-block" />
                  Placez l'étiquette dans le cadre
                </span>
              )}
              {scannerStatus === "detected" && (
                <span className="flex items-center gap-2 text-green-300 text-sm bg-green-900/60 px-3 py-1.5 rounded-full font-medium">
                  ✓ Code détecté — recherche en cours…
                </span>
              )}
              {/* LOT 4 — Aide visuelle distance */}
              {scannerStatus === "scanning" && hintLevel === 1 && (
                <span className="text-white/45 text-xs bg-black/30 px-2.5 py-1 rounded-full">
                  🔍 Approchez l'étiquette à 10-15 cm
                </span>
              )}
              {scannerStatus === "scanning" && hintLevel === 2 && (
                <span className="text-amber-300/80 text-xs bg-black/50 px-2.5 py-1 rounded-full text-center max-w-[260px]">
                  Pas de détection — approchez davantage l'étiquette et assurez-vous qu'elle est bien dans le cadre
                </span>
              )}
            </div>

            {/* Overlay de chargement (lookup en cours) */}
            {isLooking && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                <div className="text-center space-y-3">
                  <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="text-white text-sm">Recherche du robot…</p>
                </div>
              </div>
            )}

            {/* ---------------------------------------------------------------
             * PARTIE 1 — Compteur de frames avec état BarcodeDetector explicite
             * --------------------------------------------------------------- */}
            <div className="absolute bottom-2 left-0 right-0 flex flex-col items-center gap-0.5 pointer-events-none">
              <span className="text-white/40 text-[10px] font-mono bg-black/40 px-2 py-0.5 rounded">
                Frames : {frameCount}{usedEngine ? ` · ${engineDiagLabel()}` : ""}
              </span>
              {usedEngine === "zxing" && diagnosticReason && (
                <span className="text-amber-400/70 text-[9px] font-mono bg-black/40 px-2 py-0.5 rounded">
                  BarcodeDetector : {diagnosticReason}
                </span>
              )}
            </div>

            {/* ---------------------------------------------------------------
             * PARTIE 5 — Overlay debug complet (?debug=1)
             * --------------------------------------------------------------- */}
            {isDebugMode && (
              <div className="absolute top-2 left-2 right-2 bg-black/75 border border-white/20 rounded-lg p-3 pointer-events-none text-[10px] font-mono text-white/80 space-y-0.5">
                <div className="text-white/50 text-[9px] uppercase tracking-widest mb-1">
                  🔍 Mode debug scanner
                </div>
                <div>
                  <span className={usedEngine === "native" ? "text-green-400" : "text-amber-400"}>
                    Engine: {usedEngine || "…"}
                  </span>
                </div>
                <div>
                  BarcodeDetector:{" "}
                  {usedEngine === "native"
                    ? <span className="text-green-400">instancié OK</span>
                    : <span className="text-red-400">{diagnosticReason || "non utilisé"}</span>}
                </div>
                {supportedFormats.length > 0 && (
                  <div>Formats: {supportedFormats.join(", ")}</div>
                )}
                {videoResolution && <div>Résolution vidéo: {videoResolution}</div>}
                <div>Frames analysées: {frameCount}</div>
                {lastDetectTime > 0 && (
                  <div>Dernière détection: {Math.round((Date.now() - lastDetectTime) / 100) / 10}s</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ---- Derniers scans (phase scanning uniquement) ---- */}
        {lastScans.length > 0 && phase === "scanning" && !isLooking && (
          <div className="px-4 pb-2 shrink-0 bg-black/60">
            <p className="text-white/40 text-xs mb-1.5">Derniers scans</p>
            <div className="flex flex-wrap gap-2">
              {lastScans.map((sn) => (
                <button
                  key={sn}
                  onClick={() => triggerScan(sn)}
                  className="font-mono text-xs bg-white/10 text-white/70 px-2.5 py-1 rounded-full hover:bg-white/20 transition-colors"
                >
                  {sn}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ---- Contrôles bas (phase scanning) ---- */}
        {phase === "scanning" &&
          scannerStatus !== "permission_denied" &&
          scannerStatus !== "error" && (
          <div className="flex items-center justify-around px-6 py-4 bg-black/80 shrink-0">
            {torchSupported ? (
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "w-14 h-14 rounded-full",
                  torchOn ? "bg-yellow-500/20 text-yellow-400" : "bg-white/10 text-white"
                )}
                onClick={toggleTorch}
                data-testid="button-torch-toggle"
                title={torchOn ? "Éteindre la lampe" : "Allumer la lampe"}
              >
                <span className="text-xl">🔦</span>
              </Button>
            ) : (
              <div className="w-14 h-14" />
            )}

            <Button
              variant="ghost"
              size="icon"
              className="w-14 h-14 rounded-full bg-white/10 text-white hover:bg-white/20"
              onClick={() => setPhase("manual")}
              data-testid="button-manual-mode"
              title="Saisie manuelle"
            >
              <Keyboard className="w-6 h-6" />
            </Button>

            <div className="w-14 h-14" />
          </div>
        )}
      </div>
    </div>
  );
}
